import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import Processos from "@/pages/Processos";
import Dossier from "@/pages/Dossier";
import NovoProcesso from "@/pages/NovoProcesso";
import Calendario from "@/pages/Calendario";
import Definicoes from "@/pages/Definicoes";
import Contactos from "@/pages/Contactos";
import Landing, { FaqPage } from "@/pages/Landing";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import Visao from "@/pages/Visao";
import Equipa from "@/pages/Equipa";
import ArquivoKyc from "@/pages/ArquivoKyc";
import MeuDia from "@/pages/MeuDia";
import { RemindHost } from "@/components/RemindPreview";
import ShortLink, { subdomainCode } from "@/pages/ShortLink";
import Obrigacoes from "@/pages/Obrigacoes";
import { RobotsMeta } from "@/components/RobotsMeta";
import Painel from "@/pages/Painel";
import Escrituras from "@/pages/Escrituras";

function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "direccao" ? "/visao" : user?.role === "consultor" ? "/dia" : "/painel"} replace />;
}
import ClientPortal from "@/pages/ClientPortal";

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-ec-bg">
      <div className="font-heading text-2xl text-ec-green animate-pulse">ImoDesk</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/entrar" replace />;
  if (user.role === "platform_admin") return <Navigate to="/admin" replace />;
  return children;
}

function PublicHome() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Landing />;
  return <Navigate to={user.role === "platform_admin" ? "/admin" : "/inicio"} replace />;
}

function AdminOnly() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/entrar" replace />;
  return user.role === "platform_admin" ? <Admin /> : <Navigate to="/inicio" replace />;
}

function RoleOnly({ roles, children }) {
  const { user } = useAuth();
  return roles.includes(user?.role) ? children : <Navigate to="/inicio" replace />;
}

function ImpicOnly({ children }) {
  const { user } = useAuth();
  return user?.impic_access ? children : <Navigate to="/inicio" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/entrar" element={<Login />} />
      <Route path="/login" element={<Navigate to="/entrar" replace />} />
      <Route path="/convite/:token" element={<AcceptInvite />} />
      <Route path="/repor/:token" element={<ResetPassword />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
      <Route path="/:slug/p/:code" element={<ShortLink />} />
      <Route path="/:slug/entrar" element={<Login />} />
      <Route path="/c/:code" element={<ShortLink />} />
      {subdomainCode() && <Route path="/:code" element={<ShortLink />} />}
      <Route path="/calendario/:token" element={<Calendario />} />
      <Route path="/perguntas" element={<FaqPage />} />
      <Route path="/termos" element={<Legal doc="termos" />} />
      <Route path="/privacidade" element={<Legal doc="privacidade" />} />
      <Route path="/seguranca" element={<Legal doc="seguranca" />} />
      <Route path="/en/terms" element={<Legal doc="en-terms" />} />
      <Route path="/en/privacy" element={<Legal doc="en-privacy" />} />
      <Route path="/en/security" element={<Legal doc="en-security" />} />
      <Route path="/admin" element={<AdminOnly />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/processos" element={<RoleOnly roles={["gestor", "consultor", "direccao"]}><Processos /></RoleOnly>} />
        <Route path="/processo/:id" element={<Dossier />} />
        <Route path="/hoje" element={<Navigate to="/painel" replace />} />
        <Route path="/painel" element={<RoleOnly roles={["gestor"]}><Painel /></RoleOnly>} />
        <Route path="/escrituras" element={<RoleOnly roles={["gestor"]}><Escrituras /></RoleOnly>} />
        <Route path="/dia" element={<RoleOnly roles={["consultor"]}><MeuDia /></RoleOnly>} />
        <Route path="/inicio" element={<HomeRedirect />} />
        <Route path="/visao" element={<RoleOnly roles={["direccao"]}><Visao /></RoleOnly>} />
        <Route path="/equipa" element={<RoleOnly roles={["direccao"]}><Equipa /></RoleOnly>} />
        <Route path="/arquivo-kyc" element={<RoleOnly roles={["direccao"]}><ArquivoKyc /></RoleOnly>} />
        <Route path="/direccao" element={<Navigate to="/visao" replace />} />
        <Route path="/novo" element={<RoleOnly roles={["consultor"]}><NovoProcesso /></RoleOnly>} />
        <Route path="/nova-angariacao" element={<Navigate to="/novo" replace />} />
        <Route path="/obrigacoes" element={<ImpicOnly><Obrigacoes /></ImpicOnly>} />
        <Route path="/definicoes" element={<RoleOnly roles={["direccao"]}><Definicoes /></RoleOnly>} />
        <Route path="/contactos" element={<Contactos />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RobotsMeta />
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <RemindHost />
      </BrowserRouter>
    </AuthProvider>
  );
}
